import { useState } from "react";
import { ChevronDown } from "lucide-react";

const FAQ = [
  {
    q: "How much does it cost — including travel and rush fees?",
    a: (
      <>
        Pricing is fixed by property size (see the{" "}
        <a href="/pricing" className="text-teal-700 underline hover:text-stone-900">
          Pricing page
        </a>
        ). We keep fees simple:
        <ul className="mt-3 space-y-1.5 text-stone-700 list-disc pl-5">
          <li>
            <strong>Travel fee</strong> — 10% for anywhere in MD or VA outside
            the immediate DC region (roughly outside the Woodbridge circle on
            the map above).
          </li>
          <li>
            <strong>Rush fee</strong> — 50% for requests needed within 48 hours
            of contact.
          </li>
          <li>
            <strong>Cancellation</strong> — free before we&apos;re on the road;
            50% of the base fee if we&apos;ve already dispatched.
          </li>
        </ul>
      </>
    ),
  },
  {
    q: "Are you an architecture firm?",
    a: (
      <>
        Nope — and we&apos;re proud of that. We&apos;re a floor plan drafting
        studio. We measure existing properties on-site and produce
        MLS-ready 2D, colorized, and 3D floor plans for real-estate use.
        For structural work, permitting, or new construction you&apos;ll want
        a licensed architect; we&apos;re happy to recommend one in the DMV.
      </>
    ),
  },
  {
    q: "How fast is turnaround?",
    a: (
      <>
        Standard turnaround is <strong>24–48 hours</strong> after we&apos;ve
        been on-site. RLAH agents get priority queue on standard 2D &amp;
        3D orders.
      </>
    ),
  },
  {
    q: "How many revisions do I get?",
    a: (
      <>
        Every plan includes <strong>5 rounds of revisions</strong> at no extra
        cost. Send us tweaks — room labels, dimensions, layout adjustments —
        and we&apos;ll turn them around fast.
      </>
    ),
  },
  {
    q: "What file formats do I get?",
    a: (
      <>
        Every plan is delivered as <strong>PDF</strong>, <strong>PNG</strong>,
        and <strong>DXF</strong>. Drafted to ANSI/REBAC standards. Ready to
        drop straight into your MLS listing.
      </>
    ),
  },
];

export default function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <section
      id="faq"
      className="py-24 lg:py-32 px-6 lg:px-12 bg-[#FAF7F2]"
      data-testid="faq-section"
    >
      <div className="mx-auto max-w-4xl">
        <div className="text-center mb-12">
          <span className="inline-block bg-white border border-stone-200 px-3 py-1.5 rounded-full text-xs font-semibold text-stone-700 mb-5">
            05 — Fees &amp; FAQ
          </span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
            Fair fees.
            <br />
            <span className="italic font-medium text-teal-700">
              Straight answers.
            </span>
          </h2>
        </div>

        <div className="bg-white border border-stone-200 rounded-[2rem] overflow-hidden divide-y divide-stone-200">
          {FAQ.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={i} data-testid={`faq-item-${i}`}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  aria-expanded={isOpen}
                  className="w-full text-left px-6 lg:px-8 py-5 flex items-start gap-4 hover:bg-stone-50 transition-colors"
                >
                  <span className="text-[10px] font-mono text-stone-400 mt-1.5 flex-shrink-0 w-6">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="flex-1 font-display text-lg font-semibold text-stone-900">
                    {item.q}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 text-stone-500 flex-shrink-0 mt-1 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div
                    className="px-6 lg:px-8 pb-6 -mt-2 pl-16 text-stone-600 leading-relaxed"
                    data-testid={`faq-answer-${i}`}
                  >
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
